import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef } from "react";
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Link2, Undo2, Redo2, X } from "lucide-react";

/**
 * Lightweight TipTap rich-text editor for policy pages.
 * onUpdate fires on every change with the current HTML.
 */
export default function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // StarterKit v3 ships with a Link extension, so we just configure it here.
        link: {
          openOnClick: false,
          HTMLAttributes: { class: "underline text-[#F5D565]" },
        },
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none focus:outline-none min-h-[260px] py-4",
        "data-testid": "richtext-editor",
      },
    },
  });

  // Sync external value changes (e.g. switching tabs)
  const lastValueRef = useRef(value);
  useEffect(() => {
    if (editor && value !== lastValueRef.current && value !== editor.getHTML()) {
      lastValueRef.current = value;
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="border border-[#262626]" data-testid="richtext-wrapper">
      <div className="flex flex-wrap gap-1 border-b border-[#262626] p-2 bg-[#141414]">
        <Btn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold" tid="rt-bold"><Bold size={14} /></Btn>
        <Btn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic" tid="rt-italic"><Italic size={14} /></Btn>
        <Btn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2" tid="rt-h2"><Heading2 size={14} /></Btn>
        <Btn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3" tid="rt-h3"><Heading3 size={14} /></Btn>
        <Btn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bulleted list" tid="rt-ul"><List size={14} /></Btn>
        <Btn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list" tid="rt-ol"><ListOrdered size={14} /></Btn>
        <Btn active={editor.isActive("link")} onClick={setLink} title="Link" tid="rt-link"><Link2 size={14} /></Btn>
        <span className="flex-1" />
        <Btn onClick={() => editor.chain().focus().undo().run()} title="Undo" tid="rt-undo"><Undo2 size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} title="Redo" tid="rt-redo"><Redo2 size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting" tid="rt-clear"><X size={14} /></Btn>
      </div>
      <div className="px-4 bg-[#0A0A0A] min-h-[280px]">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Btn({ active, onClick, title, tid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={tid}
      className="font-ui inline-flex items-center justify-center transition-all"
      style={{
        width: 32, height: 32,
        background: active ? "#F5D565" : "transparent",
        color: active ? "#0A0A0A" : "rgba(255,255,255,0.85)",
        border: "1px solid " + (active ? "#F5D565" : "#262626"),
      }}
    >
      {children}
    </button>
  );
}
